import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Button, Spinner } from 'react-bootstrap';
import { colors, radius, shadow, spacing, transition, typography } from '../tokens';

// PrimaryButton — wrap react-bootstrap Button với:
//   - Variant primary/secondary/success/danger/ghost
//   - `loading` prop tự hiển thị spinner + disable + giữ chiều rộng
//     (label vẫn render bằng visibility:hidden để không "nhảy" layout)
//   - `block` prop full-width
//   - `size` xs/sm/md/lg
const PrimaryButton = forwardRef(function PrimaryButton(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    block = false,
    disabled,
    children,
    loadingText,
    ...props
  },
  ref
) {
  return (
    <StyledButton
      ref={ref}
      $variant={variant}
      $size={size}
      $block={block}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <>
          <Spinner size='sm' animation='border' role='status' aria-hidden='true' />
          <span>{loadingText || children}</span>
        </>
      ) : (
        <span>{children}</span>
      )}
    </StyledButton>
  );
});

const SIZES = {
  xs: { padY: '4px', padX: '10px', font: typography.size.xs },
  sm: { padY: '6px', padX: '12px', font: typography.size.sm },
  md: { padY: '10px', padX: '18px', font: typography.size.base },
  lg: { padY: '12px', padX: '24px', font: typography.size.md },
};

const variantStyles = {
  primary: css`
    background: ${colors.primary};
    color: ${colors.white};
    border: 1px solid ${colors.primary};
    &:hover:not(:disabled) {
      background: ${colors.primaryHover};
      border-color: ${colors.primaryHover};
    }
    &:active:not(:disabled) {
      background: ${colors.primaryActive};
      border-color: ${colors.primaryActive};
    }
  `,
  secondary: css`
    background: ${colors.surface};
    color: ${colors.gray700};
    border: 1px solid ${colors.border};
    &:hover:not(:disabled) {
      background: ${colors.gray50};
      border-color: ${colors.borderHover};
      color: ${colors.gray900};
    }
  `,
  success: css`
    background: ${colors.success};
    color: ${colors.white};
    border: 1px solid ${colors.success};
    &:hover:not(:disabled) {
      filter: brightness(0.95);
    }
  `,
  danger: css`
    background: ${colors.danger};
    color: ${colors.white};
    border: 1px solid ${colors.danger};
    &:hover:not(:disabled) {
      filter: brightness(0.95);
    }
  `,
  ghost: css`
    background: transparent;
    color: ${colors.primary};
    border: 1px solid transparent;
    /* Phải explicit set color trong :hover, :focus, :active vì BS
       Button có sẵn class .btn-primary cho variant default, BS
       stylesheet load trước styled-components → .btn-primary:hover
       { color: #fff } leak ra, làm chữ trắng trên bg primarySoft khó
       đọc. Set color cùng selector + same specificity sẽ thắng nhờ
       source order (SC inject sau BS). */
    &:hover:not(:disabled),
    &:focus:not(:disabled),
    &:active:not(:disabled),
    &:hover:focus:not(:disabled) {
      background: ${colors.primarySoft};
      color: ${colors.primary};
      border-color: transparent;
    }
  `,
};

const StyledButton = styled(Button)`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  font-size: ${(p) => SIZES[p.$size].font};
  font-weight: ${typography.weight.semibold};
  padding: ${(p) => `${SIZES[p.$size].padY} ${SIZES[p.$size].padX}`};
  border-radius: ${radius.md};
  transition: ${transition.fast};
  box-shadow: ${shadow.sm};
  width: ${(p) => (p.$block ? '100%' : 'auto')};
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    box-shadow: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${(p) => variantStyles[p.$variant] || variantStyles.primary}
`;

export default PrimaryButton;
